// ./src/components/features/Tasks/TaskLocationFields/TaskLocationFields.tsx
//
// WHERE an appointment happens — distinct from who is responsible. Three modes:
//   - Venue   → pick a saved venue/vendor (with in-place create, like the responsible picker)
//   - Address → search a one-off place / café / address (captures address + coords)
//   - Online  → a meeting URL
// Self-contained: wraps its own VenuesProvider so the in-place VenueEditDrawer shares
// the list. Emits the whole location value on every change.

import React, { useMemo, useState } from "react";
import { Stack, SegmentedControl, TextInput, Loader, Input, Button, Group } from "@mantine/core";

import { VenuesProvider, useVenuesContext } from "@/contexts";
import { Venue, VENUE_KIND, TASK_LOCATION_KIND, WHOLE_SET } from "@/utils/api";
import { AppIcon, PlaceAutocompleteInput, SelectField } from "@/components/generic";
import { PlaceData } from "@/hooks/useGoogleMapsAutocomplete";
import { DRAWER_COMBOBOX_PROPS, formatPlaceLineFromPlace } from "@/utils";
import { VenueEditDrawer } from "@/components/features/Venues";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

const { VENUE, ADDRESS, ONLINE } = TASK_LOCATION_KIND;
const NONE = "none";

const LOCATION_PLACE_TYPES = ["establishment", "geocode"];

export interface TaskLocationValue {
  kind: string | null;
  venueId: string | null;
  address: string;
  latitude: number | null;
  longitude: number | null;
  url: string;
}

export interface TaskLocationFieldsProps {
  value: TaskLocationValue;
  onChange: (next: TaskLocationValue) => void;
  disabled?: boolean;
  /** Snapshot label to show for a venue that isn't in the loaded list yet. */
  fallbackVenueLabel?: string | null;
  error?: React.ReactNode;
}

// A Google Maps link for a location: precise pin from coords, else a text search.
const buildMapsUrl = (address: string, lat: number | null, lng: number | null): string | null => {
  if (typeof lat === "number" && typeof lng === "number") {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  const q = address.trim();
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
};

const TaskLocationFieldsInner: React.FC<TaskLocationFieldsProps> = ({
  value,
  onChange,
  disabled,
  fallbackVenueLabel,
  error,
}) => {
  const { venues, loading } = useVenuesContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // #142 — may this person add one here? (never for a crew member: it would land in their OWN account).
  const canCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);

  const kind = value.kind ?? null;

  const venueData = useMemo(() => {
    const items = venues
      .filter((v) => !!v.id)
      .map((v) => ({ value: v.id as string, label: `${v.name || "Untitled"}${v.kind === VENUE_KIND.VENDOR ? " · Vendor" : ""}` }));

    // Keep the selected venue visible even if it hasn't loaded into the list.
    if (value.venueId && !items.some((o) => o.value === value.venueId)) {
      items.unshift({ value: value.venueId, label: fallbackVenueLabel || "Selected venue" });
    }

    return items;
  }, [venues, value.venueId, fallbackVenueLabel]);

  const setKind = (next: string) => {
    if (next === NONE) {
      onChange({ kind: null, venueId: null, address: "", latitude: null, longitude: null, url: "" });
      return;
    }
    onChange({ ...value, kind: next });
  };

  const handleVenue = (id: string | null) => {
    onChange({ ...value, venueId: id });
  };

  const handleVenueCreated = (venue: Venue) => {
    setDrawerOpen(false);
    if (venue.id) {
      onChange({ ...value, venueId: venue.id });
    }
  };

  const handleAddress = (address1: string, place: PlaceData | null) => {
    if (place) {
      onChange({
        ...value,
        address: formatPlaceLineFromPlace(place) || address1,
        latitude: typeof place.address?.latitude === "number" ? place.address.latitude : value.latitude,
        longitude: typeof place.address?.longitude === "number" ? place.address.longitude : value.longitude,
      });
      return;
    }
    // Manual typing invalidates any previously-picked coordinates.
    onChange({ ...value, address: address1, latitude: null, longitude: null });
  };

  const mapsUrl = buildMapsUrl(value.address, value.latitude, value.longitude);

  return (
    <Input.Wrapper label="Where" description="Where this appointment takes place." error={error}>
      <Stack gap="xs" mt={4}>
        <SegmentedControl
          fullWidth
          value={kind ?? NONE}
          onChange={setKind}
          disabled={disabled}
          data={[
            { value: NONE, label: "None" },
            { value: VENUE, label: "Venue" },
            { value: ADDRESS, label: "Address" },
            { value: ONLINE, label: "Online" },
          ]}
        />

        {kind === VENUE ? (
          <SelectField
            placeholder={loading ? "Loading…" : "Pick a venue or vendor"}
            data={venueData}
            value={value.venueId}
            onChange={(_n, id) => handleVenue(id)}
            createLabel={canCreate ? "Add a new venue / vendor" : undefined}
            onCreate={canCreate ? () => setDrawerOpen(true) : undefined}
            searchable
            clearable
            disabled={disabled}
            nothingFoundMessage="No venues yet — add one"
            leftSection={<AppIcon iconName="MapPin" size={16} />}
            rightSection={loading ? <Loader size="md" /> : undefined}
            comboboxProps={DRAWER_COMBOBOX_PROPS}
            size="md"
          />
        ) : null}

        {kind === ADDRESS ? (
          <>
            <PlaceAutocompleteInput
              label="Address"
              placeholder="Search a place, café or address"
              value={value.address}
              onChange={handleAddress}
              placeTypes={LOCATION_PLACE_TYPES}
              disabled={disabled}
              withinPortal
              size="md"
            />
            {mapsUrl ? (
              <Group>
                <Button
                  variant="light"
                  size="md"
                  leftSection={<AppIcon iconName="MapPin" size={14} />}
                  component="a"
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in Google Maps
                </Button>
              </Group>
            ) : null}
          </>
        ) : null}

        {kind === ONLINE ? (
          <TextInput
            placeholder="https://meet.example.com/…"
            value={value.url}
            onChange={(e) => onChange({ ...value, url: e.currentTarget.value })}
            disabled={disabled}
            leftSection={<AppIcon iconName="Video" size={16} />}
            size="md"
          />
        ) : null}

        <VenueEditDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} kind={VENUE_KIND.VENUE} onCreated={handleVenueCreated} />
      </Stack>
    </Input.Wrapper>
  );
};

export const TaskLocationFields: React.FC<TaskLocationFieldsProps> = (props) => {
  return (
    <VenuesProvider pageSize={WHOLE_SET}>
      <TaskLocationFieldsInner {...props} />
    </VenuesProvider>
  );
};

export default TaskLocationFields;
